import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Heading,
  HStack,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { financeApi } from "./financeApi";

const money = (value: number) => `MWK ${Number(value || 0).toLocaleString()}`;

const getStoredUserId = () => {
  const direct =
    localStorage.getItem("uid") ||
    localStorage.getItem("userId") ||
    localStorage.getItem("driverId");

  if (direct) {
    return direct;
  }

  try {
    const user = localStorage.getItem("user");
    if (user) {
      const parsed = JSON.parse(user);
      return parsed?.uid || parsed?.userId || "";
    }
  } catch {
    // ignore
  }

  return "";
};

const DriverFinanceDashboard: React.FC = () => {
  const toast = useToast();
  const initialDriverId = useMemo(() => getStoredUserId(), []);
  const [driverId, setDriverId] = useState(initialDriverId);
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState<any>(null);

  const load = async (id = driverId) => {
    if (!id) {
      setPayload(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await financeApi.getDriverFinance(id);
      setPayload(res?.data || null);
    } catch (error: any) {
      toast({
        status: "error",
        title: "Failed to load driver finance",
        description: error?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialDriverId) {
      load(initialDriverId);
    } else {
      setLoading(false);
    }
  }, [initialDriverId]);

  return (
    <Box p={6}>
      <Heading size="lg" mb={6}>
        Driver Finance Dashboard
      </Heading>

      <HStack mb={6} spacing={3}>
        <Input
          placeholder="Driver ID"
          value={driverId}
          onChange={(e) => setDriverId(e.target.value)}
        />
        <Button onClick={() => load()}>Load</Button>
      </HStack>

      {loading ? (
        <VStack py={10}>
          <Spinner />
          <Text>Loading driver finance...</Text>
        </VStack>
      ) : !payload ? (
        <Text>Enter a driver ID to load finance data.</Text>
      ) : (
        <>
          <HStack spacing={8} mb={6} flexWrap="wrap">
            <Text><b>Eligible:</b> {money(payload.totalEligible || 0)}</Text>
            <Text><b>Pending:</b> {money(payload.totalPending || 0)}</Text>
            <Text><b>Paid Out:</b> {money(payload.totalPaidOut || 0)}</Text>
          </HStack>

          <Box mb={8} overflowX="auto">
            <Heading size="sm" mb={3}>
              Earnings
            </Heading>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Source Type</Th>
                  <Th>Source ID</Th>
                  <Th>Order</Th>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(payload.earnings || []).map((row: any) => (
                  <Tr key={row._id}>
                    <Td>{row.sourceType}</Td>
                    <Td>{row.sourceId}</Td>
                    <Td>{row.orderId || "-"}</Td>
                    <Td>{money(row.amount)}</Td>
                    <Td>{row.status}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          <Box overflowX="auto">
            <Heading size="sm" mb={3}>
              Payouts
            </Heading>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                  <Th>Transfer Ref</Th>
                  <Th>Batch</Th>
                  <Th>Paid At</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(payload.payouts || []).map((row: any) => (
                  <Tr key={row._id}>
                    <Td>{money(row.amount)}</Td>
                    <Td>{row.status}</Td>
                    <Td>{row.transferReference || "-"}</Td>
                    <Td>{row.payoutBatchId || "-"}</Td>
                    <Td>{row.paidAt ? new Date(row.paidAt).toLocaleString() : "-"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DriverFinanceDashboard;